import React, { useCallback, useMemo } from 'react'

import { FieldType } from 'common/fields/entities/enums'
import { Field } from 'common/fields/entities/field'
import { FilterCondition } from 'common/filter/interfaces'
import {
  OffsetOperations,
  FilterField,
  OffsetOperationsTypes,
} from 'common/filter/models/AudienceFilter/constants'
import { SECONDS_IN_DAY } from 'components/DelayPicker/constants'
import { DelayPicker } from 'components/DelayPicker/DelayPicker'
import { Select } from 'components/forms/Select/Select'

import cm from './OffsetControl.module.css'

interface OffsetControlProps {
  condition: FilterCondition
  fieldDesc: Field
  onChange: (data: Partial<FilterCondition>, closePopover?: boolean) => void
}

/**
 * Get field data type from CUF/SUF/GUF.
 */
const getFieldType = (type: FieldType | FilterField) => {
  if (type === FilterField.CURRENT_DATETIME) {
    return FieldType.DATETIME
  }

  return type.slice(type.indexOf('_') + 1) as FieldType
}

export const OffsetControl = ({ condition, fieldDesc, onChange }: OffsetControlProps) => {
  const handleOffsetOperatorChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const { offset } = condition
      const { value } = event.target

      const newCondition = {
        offset: {
          ...offset,
          operator: value,
          value: offset?.value as number,
        },
      }

      onChange(newCondition, false)
    },
    [condition, onChange],
  )

  const handleDelayOffsetChange = useCallback(
    (value: number) => {
      const newCondition = {
        offset: {
          ...condition.offset,
          operator: condition.offset?.operator ?? OffsetOperationsTypes.PLUS,
          value,
        },
      }
      onChange(newCondition)
    },
    [condition.offset, onChange],
  )

  const offsetValue = useMemo(() => {
    return (
      Math.abs(condition.offset?.value || 0) ||
      Math.abs((condition.value as number) || 0) ||
      SECONDS_IN_DAY
    )
  }, [condition.offset?.value, condition.value])

  const offsetUnits = useMemo(() => {
    const units: { days: boolean; hours?: boolean; minutes?: boolean } = { days: true }

    if (getFieldType(fieldDesc.type) === FieldType.DATETIME) {
      units.hours = true
      units.minutes = true
    }

    return units
  }, [fieldDesc.type])

  return (
    <>
      <Select
        className="m-r-xs"
        id="select-offset-operator"
        options={OffsetOperations}
        onChange={handleOffsetOperatorChange}
        value={condition.offset?.operator}
      />

      <DelayPicker
        className={cm.delayPicker}
        delay={offsetValue}
        onChange={handleDelayOffsetChange}
        {...offsetUnits}
      />
    </>
  )
}
